/**
 * Thin wrappers around `/wp-json/ez-jwt/v1/auth/*`.
 *
 * Each function takes an ApiClient (so tests inject mocks) and the
 * minimal shape needed for the call. The client handles Bearer, CSRF,
 * cookies, and refresh-on-401 — these wrappers only know paths and
 * schemas.
 *
 * **CSRF on login/refresh:** the server mirrors the rotating CSRF token
 * into the `X-EZJWT-CSRF` response header (also Set-Cookie'd, but the
 * cookie isn't reliably JS-readable when SPA and API live on different
 * hosts — see notes in session.store). We pull it from the header here
 * and surface it so callers can persist.
 */
import type { ApiClient } from '@/shared/api'
import { authPaths } from '@/shared/api'
import {
  loginResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  refreshResponseSchema,
  type LoginResponse,
  type LogoutResponse,
  type MeResponse,
  type RefreshResponse,
} from '@/shared/api/schemas/auth'

export interface LoginPayload {
  email: string
  password: string
  /** Defaults to "web" on the server side; we keep it explicit here. */
  client?: 'web' | 'mobile'
}

/** Lowercased — axios lowercases inbound headers. */
const CSRF_HEADER = 'x-ezjwt-csrf'

/** Login response augmented with the CSRF token pulled from the header. */
export interface LoginResponseWithCsrf extends LoginResponse {
  /** Mirrored from `X-EZJWT-CSRF` response header. May be null if absent. */
  csrf: string | null
}

/** Refresh response augmented with the rotated CSRF token. */
export interface RefreshResponseWithCsrf extends RefreshResponse {
  csrf: string | null
}

export async function loginRequest(
  api: ApiClient,
  payload: LoginPayload,
): Promise<LoginResponseWithCsrf> {
  const { data, headers } = await api.postRaw(authPaths.login, loginResponseSchema, {
    body: { client: 'web', ...payload },
    skipAuth: true,
  })
  return { ...data, csrf: headers[CSRF_HEADER] ?? null }
}

export async function refreshRequest(api: ApiClient): Promise<RefreshResponseWithCsrf> {
  // Body unused on web — refresh cookie + CSRF header are the inputs.
  const { data, headers } = await api.postRaw(authPaths.refresh, refreshResponseSchema, {
    body: { client: 'web' },
    skipAuth: true,
  })
  return { ...data, csrf: headers[CSRF_HEADER] ?? null }
}

export function logoutRequest(api: ApiClient): Promise<LogoutResponse> {
  return api.post(authPaths.logout, logoutResponseSchema, {
    body: { client: 'web' },
    skipAuth: true,
  })
}

export function meRequest(api: ApiClient): Promise<MeResponse> {
  return api.get(authPaths.me, meResponseSchema)
}
